import {
  MouseEvent,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { t } from 'i18next'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isNull from 'lodash/isNull'
import isObject from 'lodash/isObject'
import isUndefined from 'lodash/isUndefined'
import { v4 as uuid } from 'uuid'
import {
  DataGridProProps as MUIDataGridProps,
  GridCellParams,
  GridPinnedColumnFields,
  GridSortModel,
  MuiEvent,
} from '@microservices/wiskey-react-table-component'
import { useTheme } from '@mui/material/styles'
import {
  DataGridProps,
  GridCellEditStopParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridRowClassNameParams,
  GridRowParams,
  GridTreeNode,
} from '@mui/x-data-grid'

import { toggleCheckboxValue } from '@pages/ConfiguredEntity/helpers/toggleCheckboxValue'
import { useObjectSubscribeWS } from '@pages/ConfiguredEntity/hooks/useObjectSubscribeWS'

import { FormContext } from '@components/DisplayForm'
import { getExpandsParamForDropdown } from '@components/ui/ObjectPicker/helpers/getExpandsParamForDropdown'

import {
  useCreateViewCellFieldMutation,
  useExecuteCommandMutation,
  useFetchListControlOrDropdownObjectDataEnrichedMutation,
  useFetchObjectDataFilterByCodeEnrichedMutation,
  useUpdateViewCellFieldMutation,
} from '@redux/api'
import { useDeleteObjectMutation, useFetchObjectByCodeQuery } from '@redux/api/objects.api'
import {
  openFormDialog,
  setDialogDirty,
  setSelectedDialog,
} from '@redux/reducers/dialogWindowManager.reducer'
import {
  addFormHistory,
  createHistoryByForm,
  createHistoryByView,
} from '@redux/reducers/formHistory.reducer'

import {
  ActualTimeWindowInterval,
  ScrollingParams,
  SortType,
  TimeWindowInterval,
  useAppDispatch,
  useAppSelector,
  useDebounceFn,
  useGetDropdownDataFromCommand,
  useInfiniteScroll,
  usePrevious,
  usePrompt,
} from '@hooks'
import {
  checkAvailabilityEventInActions,
  createRestrictions,
  extractUrlPath,
  getCommandRequestData,
  getDropdownExpands,
  getFileNameAndExtensionFromURL,
  getFilterByQuickSearch,
  getIdForListRequest,
  getInitialPositionWindow,
  getVerifyFormCode,
  getVerifyFormTitle,
  hasObjSrcForCommandRequestData,
  isNotEmpty,
  mapCoreRowKeysToColumnCodes,
  mergeRsqlFilters,
  prepareRequestParamsWithExpands,
  renderObjectEmbeddedCell,
  selectCommandForEntityAction,
  showSnackbarNotification,
} from '@helpers'
import {
  ACTION_CODE,
  CLICK_EVENT_TYPE,
  CLICK_TYPE,
  CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME,
  DATAGRID_ROW_WINDOW_HEIGHT,
  DELETED_ROW_CLASSNAME,
  DROPDOWN_FILTER_TYPES,
  ENTITY,
  ENTITY_COMMAND_TYPE,
  ENTITY_GET_ALL_COMMAND_TYPES,
  EVENT_CODE,
  EVENT_NAME_OBJECT_MESSAGE,
  FIELD_VALUE_FORMAT,
  FIELD_VALUE_TYPE,
  FORM_TYPE,
  FOUND_SEARCH_VALUE_CLASSNAME,
  GRID_CELL_MODE,
  HOT_KEY,
  MAX_VIEW_COLUMN_WIDTH,
  MIN_VIEW_COLUMN_WIDTH,
  OBJECT_FIELD_TYPE,
  PREV_SELECTED_ROW_CLASSNAME,
  QuickRuleType,
  RULES_EXCEPTIONS,
  SEARCH_RULE_TYPE,
  SELECTED_ROW_CLASSNAME,
  SIZE_PAGE,
  SYSTEM_SELECT_COLUMN,
  UPDATE_TYPE,
  UPDATES_ROW_CLASSNAME,
} from '@constants'
import {
  ActionRow,
  ConfiguredColumn,
  DIALOG_WINDOW_TYPE,
  GETEnrichedFilteredEntityObjectDataParams,
  GETEnrichedViewObjectDataParams,
  GETObjectDataParamsEnriched,
  ObjectDataRecord,
  PassedFormData,
  POSTObjectDataFilter,
  RequestParams,
  RESTRICTION_TYPE,
  RestrictionDTO,
} from '@types'

import {
  checkIsResponseValueMatch,
  compareModels,
  findActionByEventCode,
  normalizeColumnVisibilityModel,
  buildOrderRestrictionsFromVisibleColumns,
  findPinnedColumnsDifferences,
  getColumnsInternalIds,
  getIsAvailableRuleByType,
  getSortByColumns,
  getSortKeysOfColumnsForQuickSearch,
  getSortParam,
  getTransformResponse,
  getUpdatedOptions,
  hasRedifinitionByDataType,
  isColumnPinnedByAdmin,
  isEmptyValue,
  mergeRedifinitionActions,
  objectToOptions,
  REDIFINITION_DATA_TYPE,
  transformFixRestrictions,
  validateNewRows,
} from '../helpers'
import {
  getSearchRuleForRequest,
  getSearchValueForRequest,
  getSortKeysForQuickSearchForRequest,
} from '../helpers/getSearchDataForRequest'
import { unsavedChangesRefType, useHandlersParams } from '../types'

import { useDragAndDropForRow } from './useDragAndDropForRow'
import { useQuickSearch } from './useQuickSearch'
import { useScrollDialogContainer } from './useScrollDialogContainer'

export const useHandlers = ({
  hasSearchAssistant,
  entityCode,
  entityId,
  path,
  apiRef,
  entity,
  objectCode,
  actions,
  isDropDownWindowOpen,
  getLeaveForm,
  onResetEditForm,
  onSelectPickerOption,
  initialCommonParameters: { numberOfWindows: numberOfWindowsParameter, pagination: sizeParameter },
  initialColumnVisibilityModel,
  type,
  parentFormObjectId,
  parentFormObjectCode,
  parentFormElementId,
  parentFormObjectIdType,
  valueId,
  fieldId,
  columnId,
  onMultiSelectPickerOption,
  multipleSelectPickerOption,
  objectValueNameOption,
  onSetColumnVisibilityModel,
  columns,
  entityRestrictions,
  isLoadingRestrictions,
  handleUpdateRestrictions,
  handleDeleteRestrictionsByType,
  handleOpenForm,
  search,
  isAssistiantSearch,
  dialogId,
  customPageSize,
  viewColumns,
  readonly,
  contextId,
  defaultFilter,
  outerRequestData,
  prefillCreateObjectRecordInForm,
  prefillData,
  isCtrlPressed,
  onRowClick,
  onRowSelection,
  onSortChange,
  updateDefaultRestrictions,
  defaultEntityRestrictions,
  removeDefaultRestrictions,
  removeAllRestrictions,
  outerVariables,
  outerEventBus,
  draggableRowImageRef,
}: useHandlersParams) => {
  const {
    links,
    currentLink,
    globalId: formGlobalId,
    handleSetHasUnsavedChanges: setParentFormIsDirty,
  } = useContext(FormContext)
  const theme = useTheme()

  // TODO: Избавиться ото всех локальных состояний
  const [previewFileUrl, setPreviewFileUrl] = useState<string | undefined>()
  const [showPreviewFileModal, setShowPreviewFileModal] = useState(false)
  const [isShowDeleteModal, setIsShowDeleteModal] = useState<boolean>(false)
  const [isShowUserRestrictionAsDefaultModel, setIsShowUserRestrictionAsDefaultModel] =
    useState<boolean>(false)
  const [deleteModalData, setDeleteModalData] = useState<ObjectDataRecord | null>(null)
  const [isCtrlHeld, setIsCtrlHeld] = useState(false)
  const [isShiftHeld, setIsShiftHeld] = useState(false)
  const [selectedRows, setSelectedRows] = useState<ObjectDataRecord[]>([])
  const [samePageFormsData, setSamePageFormsData] = useState<PassedFormData[]>([])
  const [currentStaticForm, setCurrentStaticForm] = useState(0)
  const [currentDynamicForm, setCurrentDynamicForm] = useState(0)
  const [isOpenShowColumnsSettings, setOpenShowColumnsSettings] = useState(false)
  const [totalElements, setTotalElements] = useState<number | null>(null)
  const [searchAssistantValue, setSearchAssistantValue] = useState<POSTObjectDataFilter>([])
  const [valueSearchFilter, setValueSearchFilter] = useState('')
  const [sortKeysForQuickSearch, setSortKeysForQuickSearch] = useState<string[]>([])
  const [searchRule, setSearchRule] = useState<QuickRuleType>({
    name: SEARCH_RULE_TYPE.CONTAIN,
    exception: RULES_EXCEPTIONS.DEFAULT,
  })
  const [highlightedSelectedRows, setHighlightedSelectedRows] = useState<ObjectDataRecord[]>([])

  const [isEditMode, setIsEditMode] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const [newRows, setNewRows] = useState<ObjectDataRecord[]>([])
  const isFirstRender = useRef(true)
  const [errorFields, setErrorFields] = useState<Record<string, boolean>>({})
  const [activeDownloadRows, setActiveDownloadRows] = useState<Record<string, boolean>>({})

  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [hasPendingRefresh, setHasPendingRefresh] = useState(false)
  const [localUpdateType, setLocalUpdateType] = useState<UPDATE_TYPE>()
  const [externalActionData, setExternalActionData] = useState<ObjectDataRecord[]>([])

  const [prevDefaultFilter, clearId] = usePrevious(defaultFilter)

  const hasQuickSearch = Boolean(entity?.hasQuickSearch)
  const hasWrapRowText = Boolean(entity?.hasWrapRowText)
  const hasWrapHeaderText = Boolean(entity?.hasWrapHeaderText)
  const headerHeight = entity?.headerHeight
  const headerGroupHeight = entity?.headerGroupHeight
  const isEnabledLocalParameters = entity?.useLocalParameters
  const hasNewRows = Boolean(newRows.length)
  const hasExternalActionData = Boolean(externalActionData.length)
  const missingRequiredFields = useRef<{ [key: string]: boolean }>({})
  const unsavedChangesRef = useRef<unsavedChangesRefType>({
    rows: {},
    originalRows: {},
    labels: {},
  })
  const allowEditMode = useMemo(() => columns.some(column => column.editable), [columns])
  const hasErrorFields = Boolean(Object.keys(errorFields).length)

  const [deleteObject] = useDeleteObjectMutation()
  const [executeCommand] = useExecuteCommandMutation()
  const [updateViewCellField] = useUpdateViewCellFieldMutation()
  const [createViewCellField] = useCreateViewCellFieldMutation()

  const objectsWithDirtyRecords = useAppSelector(
    state => state.changedViewRows.objectsWithDirtyRecords
  )
  const dialogWindows = useAppSelector(state => state.dialogWindowManager.dialogWindows)

  // TODO Нужно использовать хук usePK для вытаскивания primary key филда
  const selectedRowIds = useMemo(
    () =>
      selectedRows.map(
        ({ _id, [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id }) =>
          String(_id ?? id)
      ),
    [selectedRows]
  )

  const selectedRowIdsSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds])

  const highlightedSelectedRowsIds = useMemo(
    () =>
      highlightedSelectedRows.map(
        ({ _id, [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id }) => _id ?? id
      ),
    [selectedRows]
  )

  const [prevSelectedRowId, setPrevSelectedRowId] = useState<string | undefined>(undefined) // для подсвечивания row другим цветом при возвращении в список

  const dispatch = useAppDispatch()

  //скип и перекрытие дефолтной загрузки вью, список вызывается из команды
  const hasRedifinitionCommands = hasRedifinitionByDataType(
    outerRequestData,
    REDIFINITION_DATA_TYPE.COMMANDS,
    ENTITY_GET_ALL_COMMAND_TYPES
  )

  const hasRedifinitionActions = hasRedifinitionByDataType(
    outerRequestData,
    REDIFINITION_DATA_TYPE.ACTIONS
  )

  const actionsForConfig = hasRedifinitionActions
    ? mergeRedifinitionActions(actions ?? [], outerRequestData?.redifinitions?.[0]?.actions ?? [])
    : actions

  const hasSetDataAction = actionsForConfig?.some(
    action => action.actionCode === ACTION_CODE.SET_DATA
  )

  const hasOuterOnRowSelectionEvent = checkAvailabilityEventInActions(
    mergeRedifinitionActions(actions ?? [], outerRequestData?.redifinitions?.[0]?.actions ?? []),
    EVENT_CODE.ON_ROW_SELECTION
  )

  const hasOnRowSelectionEvent = checkAvailabilityEventInActions(
    actions,
    EVENT_CODE.ON_ROW_SELECTION
  )

  const hasDragStartEvent = checkAvailabilityEventInActions(actions, EVENT_CODE.ON_DRAG_START)

  const hasDropEvent = checkAvailabilityEventInActions(actions, EVENT_CODE.ON_DROP)

  const currentDropDownObjectCode =
    type === ENTITY.DROP_DOWN_ENTITY ? entity?.objectCode : undefined

  const { data: dropDownWindowObjectModel } = useFetchObjectByCodeQuery(currentDropDownObjectCode, {
    skip: type !== ENTITY.DROP_DOWN_ENTITY || !currentDropDownObjectCode,
  })

  const dropDownWindowCommand = useMemo(() => {
    if (type !== ENTITY.DROP_DOWN_ENTITY) {
      return undefined
    }

    return selectCommandForEntityAction({
      overrideCommands: entity?.commands,
      objectCommands: dropDownWindowObjectModel?.commands,
      actionType: ENTITY_COMMAND_TYPE.GET_ALL,
    })
  }, [type, entity?.commands, dropDownWindowObjectModel?.commands])

  const dropDownWindowFields = useMemo(() => {
    if (type !== ENTITY.DROP_DOWN_ENTITY) {
      return []
    }

    return (
      entity?.columns
        ?.filter(column => column.visibility)
        .map(column => column.value || column.code)
        .filter(Boolean) ?? []
    )
  }, [type, entity?.columns])

  const dropDownWindowExpands = useMemo(() => {
    if (type !== ENTITY.DROP_DOWN_ENTITY) {
      return []
    }

    const objectValueExpands = getDropdownExpands(objectValueNameOption || '')
    const columnExpands =
      entity?.columns
        ?.filter(column => column.visibility)
        .flatMap(column => {
          const fieldCode = column.value || column.code
          if (!fieldCode || !column.valueType) {
            return []
          }

          return getExpandsParamForDropdown({
            fieldCode,
            valueType: column.valueType,
            bindType: column.bindType,
            pathArray: column.pathArray ?? undefined,
          })
        }) ?? []

    return [...new Set([...objectValueExpands, ...columnExpands])]
  }, [type, objectValueNameOption, entity?.columns])

  const handleSetLocalUpdateType = (updateType: UPDATE_TYPE) => setLocalUpdateType(updateType)

  const handleSetHasUnsavedChanges = (value: boolean) => {
    setParentFormIsDirty?.(value)
    setHasUnsavedChanges(value)
  }

  const getFetchEntityObjectDataEnrichedMutation = () => {
    if (type === ENTITY.DROP_DOWN_ENTITY) {
      return useGetDropdownDataFromCommand
    }

    if (type === ENTITY.LIST_CONTROL) {
      if (hasRedifinitionCommands) {
        return useExecuteCommandMutation
      }

      return useFetchListControlOrDropdownObjectDataEnrichedMutation
    }

    return useFetchObjectDataFilterByCodeEnrichedMutation
  }

  const pageSizeForRequest = customPageSize * 2

  const commonParams = {
    contextId,
    size:
      pageSizeForRequest > Number(sizeParameter)
        ? pageSizeForRequest.toString()
        : sizeParameter || SIZE_PAGE,
    body:
      type === ENTITY.LIST_CONTROL || type === ENTITY.DROP_DOWN_ENTITY
        ? {
            searchValues: Array.isArray(searchAssistantValue) ? [...searchAssistantValue] : [],
          }
        : searchAssistantValue,
    ...(hasQuickSearch && {
      // TODO Похоже правильнее использовать стейт
      // sortKeysForQuickSearch: getSortKeysOfColumnsForQuickSearch(
      //   getInitialColumnVisibilityModel(viewColumns, true, SEARCH_RULE_TYPE.EQUAL)
      // ),
      sortKeysForQuickSearch: getSortKeysForQuickSearchForRequest(
        sortKeysForQuickSearch,
        valueSearchFilter,
        searchRule.name
      ),
      searchValue: getSearchValueForRequest(valueSearchFilter, searchRule.name),
      searchRule: getSearchRuleForRequest(valueSearchFilter, searchRule.name),
    }),
    sort: getSortByColumns(viewColumns),
  }

  const commandDropDownWindowParams: RequestParams = dropDownWindowCommand?.parameters?.length
    ? getCommandRequestData(dropDownWindowCommand.parameters, {})
    : {}

  const computedDropDownWindowParams: RequestParams = {
    ...commandDropDownWindowParams,
    fields: dropDownWindowFields,
    size:
      pageSizeForRequest > Number(sizeParameter)
        ? pageSizeForRequest.toString()
        : sizeParameter || SIZE_PAGE,
    ...(defaultFilter ? { filter: defaultFilter } : {}),
  }

  const computedDropDownWindowBody = dropDownWindowCommand?.body?.length
    ? getCommandRequestData(dropDownWindowCommand.body, {})
    : undefined

  const { requestParams: dropDownWindowRequestParams, expands: mergedDropDownWindowExpands } =
    prepareRequestParamsWithExpands(computedDropDownWindowParams, dropDownWindowExpands)

  const dropDownWindowSortParams = useMemo(() => {
    if (type !== ENTITY.DROP_DOWN_ENTITY) {
      return []
    }

    return getSortByColumns(viewColumns)
  }, [type, viewColumns])

  const dropDownWindowParamsForRequest =
    type === ENTITY.DROP_DOWN_ENTITY
      ? {
          transformResponseWithPagination: true,
          objectCode: dropDownWindowCommand?.objectCode || currentDropDownObjectCode,
          commandCode: dropDownWindowCommand?.commandCode,
          method: dropDownWindowCommand?.httpMethod,
          resource: dropDownWindowCommand?.resource,
          params: dropDownWindowRequestParams,
          body: computedDropDownWindowBody,
          expands: mergedDropDownWindowExpands,
          sort: dropDownWindowSortParams,
          skip: !dropDownWindowCommand || !currentDropDownObjectCode,
        }
      : null

  const viewParamsForRequest: GETEnrichedViewObjectDataParams & {
    skip?: boolean
  } = {
    ...commonParams,
    size: sizeParameter,
    code: entity?.objectCode,
    viewId: entityId || entity?.id,
    // для ассистента отдельный запрос в useConfiguredEntity в then у fetchEntity
    skip: !entity?.id || !contextId,
    filter: defaultFilter,
  }

  const listControlOfDropdownParamsForRequest: GETEnrichedFilteredEntityObjectDataParams & {
    skip?: boolean
  } = {
    ...commonParams,
    elementId: parentFormElementId,
    valueId,
    fieldId,
    columnId,
    filter: defaultFilter,
    formObjectCode: parentFormObjectCode,
    viewId: entityId || undefined,
    dropDownEntityType: type === ENTITY.DROP_DOWN_ENTITY ? DROPDOWN_FILTER_TYPES.WINDOW : undefined,
    cacheId: dialogId,
    skip: !entity?.id || !parentFormObjectCode || !contextId,
    ...getIdForListRequest(parentFormObjectId, parentFormObjectIdType),
  }

  usePrompt({ when: hasUnsavedChanges })

  const { handlers: dragAndDropRowHandlers, state: dragAndDropRowStates } = useDragAndDropForRow({
    hasDragStartEvent,
    hasDropEvent,
    apiRef,
    selectedRows,
    actions,
    outerVariables,
    entity,
    outerEventBus,
    draggableRowImageRef,
  })

  const getEntityParamsForRequest = () => {
    if (type === ENTITY.DROP_DOWN_ENTITY) {
      return dropDownWindowParamsForRequest
    }

    if (type === ENTITY.LIST_CONTROL) {
      if (hasRedifinitionCommands && outerRequestData) {
        const { redifinitions, srcObj } = outerRequestData
        const getAllCommand = redifinitions?.[0].commands?.find(cmd =>
          ENTITY_GET_ALL_COMMAND_TYPES.includes(cmd.type)
        )

        return {
          method: getAllCommand?.httpMethod,
          objectCode: getAllCommand?.objectCode,
          commandCode: getAllCommand?.name,
          params: getCommandRequestData(getAllCommand?.parameters ?? [], srcObj),
          body: getCommandRequestData(getAllCommand?.body ?? [], srcObj),
          transformResponseWithPagination: true,

          skip:
            !getAllCommand ||
            hasObjSrcForCommandRequestData(
              [...(getAllCommand?.parameters ?? []), ...(getAllCommand?.body ?? [])],
              srcObj
            ),
        }
      }

      return listControlOfDropdownParamsForRequest
    }

    return viewParamsForRequest
  }

  const entityRequest = getFetchEntityObjectDataEnrichedMutation()
  const entityParamsForRequest = getEntityParamsForRequest()

  const {
    combinedData: objectData,
    isLoading: isLoadingObjectData,
    totalPages,
    totalCountElements,
    refresh,
    onChangeSort,
    readMore,
    currentSort,
    setCombinedDataForce: setObjectData,
    currentPage,
    scrollingParams,
    refreshAllPages,
    isActiveTimeWindow,
  } = useInfiniteScroll<
    GETObjectDataParamsEnriched & { body?: POSTObjectDataFilter },
    ObjectDataRecord
  >(entityRequest, entityParamsForRequest, getTransformResponse, undefined, columns)

  const mappedObjectData = useMemo(() => {
    if (type !== ENTITY.DROP_DOWN_ENTITY) {
      return objectData
    }

    return objectData?.map(item => mapCoreRowKeysToColumnCodes(item, entity?.columns ?? []))
  }, [type, objectData, entity?.columns])

  const refreshObjectData = (
    params?: Omit<ScrollingParams, 'page'>,
    skip?: boolean,
    timeWindow?: ActualTimeWindowInterval,
    filter?: string
  ) => {
    if (apiRef.current && Object.keys(apiRef.current).length) {
      const isNeedResetScroll = timeWindow?.isFirstIntervalRun !== false // Не сбрасываем скролл в случае смещения временного окна по таймеру
      if (isNeedResetScroll) {
        apiRef.current.scrollToIndexes({ rowIndex: 0 })
      }
    }

    const mergedParams =
      params != null
        ? {
            ...params,
            filter: mergeRsqlFilters(
              defaultFilter,
              params.filter as string | null | undefined,
              filter
            ),
          }
        : params

    return refresh(mergedParams, skip, timeWindow)
  }

  const refreshChangeSortData = (sort: SortType[]) => {
    if (apiRef.current && Object.keys(apiRef.current).length) {
      apiRef.current.scrollToIndexes({ rowIndex: 0 })
    }

    return onChangeSort(sort, {
      sortKeysForQuickSearch: getSortKeysForQuickSearchForRequest(
        sortKeysForQuickSearch,
        valueSearchFilter,
        searchRule.name
      ),
      searchValue: getSearchValueForRequest(valueSearchFilter, searchRule.name),
      searchRule: getSearchRuleForRequest(valueSearchFilter, searchRule.name),
    })
  }

  const {
    handlers: {
      handleSetColumnNameCheckBox,
      handleSetGlobalFilter,
      handleSetSearchRule,
      handleSetSortKeysColumnCheckBox,
      handleSetSortKeysForQuickSearch,
      handleSetBooleanMode,
    },
    state: { columnNameCheckBox, sortKeysColumnCheckBox, isBooleanSearchMode },
  } = useQuickSearch({
    searchRule,
    sortKeysForQuickSearch,
    valueSearchFilter,
    viewColumns,
    entityId,
    dialogId,
    setSearchRule,
    setSortKeysForQuickSearch,
    setValueSearchFilter,
    refreshObjectData,
    entityType: type,
    handleUpdateRestrictions,
    handleDeleteRestrictionsByType,
    defaultEntityRestrictions,
    defaultFilter,
  })

  const columnNameCheckBoxRef = useRef(columnNameCheckBox)
  const sortKeysColumnCheckBoxRef = useRef(sortKeysColumnCheckBox)

  useEffect(() => {
    columnNameCheckBoxRef.current = columnNameCheckBox
  }, [columnNameCheckBox])

  useEffect(() => {
    sortKeysColumnCheckBoxRef.current = sortKeysColumnCheckBox
  }, [sortKeysColumnCheckBox])

  useEffect(() => {
    if (prevDefaultFilter === defaultFilter) return

    if (isFirstRender.current) {
      isFirstRender.current = false

      return
    }

    refresh({ filter: defaultFilter })

    return () => {
      clearId()
    }
  }, [defaultFilter])

  const setHistoryFormInForm = ({
    formCode,
    newId,
    objectId,
    event,
  }: {
    formCode: string
    newId: string
    objectId: string | null
    event: FORM_TYPE
  }) => {
    dispatch(
      addFormHistory({
        id: formGlobalId,
        form: {
          formCode,
          objectCode,
          objectId,
          event,
          title: formCode,
          id: newId,
          isWindow: false,
        },
      })
    )
  }

  const setHistoryFormByView = ({
    isWindow = false,
    formCode,
    newId,
    objectId,
    event,
  }: {
    isWindow?: boolean
    formCode: string
    newId: string
    objectId: string | null
    event: FORM_TYPE
  }) => {
    if (dialogId) {
      dispatch(
        createHistoryByView({
          id: newId,
          form: {
            formCode,
            objectCode,
            objectId,
            event,
            isWindow,
            title: formCode,
            id: newId,
          },
          view: {
            objectCode,
            title: entity?.title || '',
            viewPath: path,
            id: dialogId,
            isRootLink: true,
          },
        })
      )
    }
  }

  const setHistoryFormByForm = ({
    isWindow = false,
    formCode,
    newId,
    objectId,
    event,
  }: {
    isWindow?: boolean
    formCode: string
    newId: string
    objectId: string | null
    event: FORM_TYPE
  }) => {
    dispatch(
      createHistoryByForm({
        id: newId,
        childForm: {
          formCode,
          objectCode,
          objectId,
          event,
          title: formCode,
          id: newId,
          isWindow,
        },
        parentForm: { ...currentLink, isRootLink: true },
      })
    )
  }

  // TODO Раньше здесь был просто setState, логика с поиском была на месте вызова обработчика
  const handleSetObjectData = (newObjectData: ObjectDataRecord) => {
    setObjectData(prev => {
      const newData = [...prev]
      const currentRowIndex = newData.findIndex(row => row._id === newObjectData._id)
      newData[currentRowIndex] = { ...newData[currentRowIndex], ...newObjectData }

      return newData
    })
  }

  const handleOpenShowColumnsSettings = (value: boolean) => setOpenShowColumnsSettings(value)

  const handleSetTotalElements = (value: number) => setTotalElements(value)

  const handleChangeModelSort = (value: GridSortModel | null) => {
    if (!entity || isLoadingRestrictions || (value !== null && !value?.length)) {
      return
    }

    const sort: SortType[] = value ? getSortParam(value, apiRef) : []

    const newRestrictions: RestrictionDTO[] = sort.map((currentSorting, currentSortingOrder) =>
      createRestrictions({
        fieldName: currentSorting.field,
        fieldCode: currentSorting.field,
        viewId: entityId as string,
        restrictionType: RESTRICTION_TYPE.SORT,
        sortDirection: currentSorting.sort,
        columnToSort: currentSorting.sortKey,
        priority: currentSortingOrder,
      })
    )

    // Очистить все ограничения
    handleDeleteRestrictionsByType(RESTRICTION_TYPE.SORT).then(() => {
      // Засетить новые значения из sort
      handleUpdateRestrictions(newRestrictions).then(() => {
        refreshChangeSortData(sort)
        // Вызываем callback для перезапуска временного окна
        onSortChange?.()
      })
    })
  }

  const handleObjectDataPageChange: MUIDataGridProps['onRowsScrollEnd'] = () => {
    if (type === ENTITY.LIST_CONTROL && (hasExternalActionData || hasSetDataAction)) {
      return
    }

    readMore()
  }

  const handleClickSearchFilter = (body: POSTObjectDataFilter, timeWindow?: TimeWindowInterval) => {
    // Это условие ломает поиск по нижей View (WF-5606)
    // if (!hasSearchAssistant) {
    //   return
    // }

    const transformBody = body?.map(el => ({
      ...el,
      // сюда передать dailyRange
      objectValue: el.objectValue?.pathStr,
    }))

    refreshObjectData(
      {
        body: transformBody,
        ...(hasQuickSearch && {
          filter:
            type === ENTITY.DROP_DOWN_ENTITY || type === ENTITY.LIST_CONTROL
              ? getFilterByQuickSearch(
                  getSearchRuleForRequest(valueSearchFilter, searchRule.name) as string,
                  getSearchValueForRequest(valueSearchFilter, searchRule.name) as string,
                  getSortKeysForQuickSearchForRequest(
                    sortKeysForQuickSearch,
                    valueSearchFilter,
                    searchRule.name
                  ) as string[]
                )
              : undefined,
          // TODO поля ниже считаются deprecated
          // TODO необходимо перейти на формирование строкового фильтра (getFilterByQuickSearch)
          sortKeysForQuickSearch: getSortKeysForQuickSearchForRequest(
            sortKeysForQuickSearch,
            valueSearchFilter,
            searchRule.name
          ),
          searchValue: getSearchValueForRequest(valueSearchFilter, searchRule.name),
          searchRule: getSearchRuleForRequest(valueSearchFilter, searchRule.name),
        }),
      },
      !entityId || !entity?.id,
      timeWindow
    )
    setSearchAssistantValue(transformBody)
  }

  const handleSetIsCtrlHeld = (value: boolean) => setIsCtrlHeld(value)

  const handleSetIsShiftHeld = (value: boolean) => setIsShiftHeld(value)

  const handleSetSelectedRows = useCallback(
    (newRow: ObjectDataRecord) => {
      if (!newRow || isEditMode) return

      const isMultiSelectMode =
        multipleSelectPickerOption ||
        hasExternalActionData ||
        hasOuterOnRowSelectionEvent ||
        entity?.multiselection

      if (isMultiSelectMode) {
        startTransition(() => {
          setSelectedRows(prev => {
            const newRows = getUpdatedOptions(prev, newRow, row => {
              const rowId = row._id || row.CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID
              const newRowId = newRow._id || newRow.CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID

              return rowId === newRowId
            })

            return newRows
          })
        })

        return
      }

      startTransition(() => {
        setSelectedRows(prevArray => {
          const isSameRow = prevArray.length === 1 && prevArray[0]._id === newRow._id
          const newSelection = isSameRow ? [] : [newRow]

          if (onRowSelection) {
            onRowSelection(isSameRow ? null : newRow)
          }

          return newSelection
        })
      })

      const shouldSkipScrollForListControl =
        type === ENTITY.LIST_CONTROL && (hasExternalActionData || hasSetDataAction)

      if (newRow?._id && !shouldSkipScrollForListControl) {
        apiRef.current.scrollToIndexes({
          rowIndex: apiRef.current.getRowIndexRelativeToVisibleRows(newRow?._id),
        })
      }
    },
    [
      isEditMode,
      multipleSelectPickerOption,
      hasExternalActionData,
      hasSetDataAction,
      hasOuterOnRowSelectionEvent,
      entity?.multiselection,
      onRowSelection,
      type,
      apiRef,
    ]
  )

  // Для сброса динамических и статических окон
  const resetDynamicAndStaticForms = () => {
    // setSelectedRowsId([])
    setSelectedRows([])
    setCurrentStaticForm(0)
    setCurrentDynamicForm(0)
  }

  const handleStaticFormChange = (newValue: number) => {
    setCurrentStaticForm(newValue)
  }

  const handleDynamicFormChange = (newValue: number) => {
    setCurrentDynamicForm(newValue)
  }

  const handleCloseCurrentFormTab = () => {
    if (samePageFormsData.length === 1) {
      // setSelectedRowsId([])
      setSelectedRows([])
      setCurrentDynamicForm(0)
      setCurrentStaticForm(0)
    }
  }

  const handleClearSamePageForms = () => {
    // setSelectedRowsId([])
    setSelectedRows([])
  }

  const handleEdit = (
    params: GridCellParams<any, ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    const row = params.row

    const formCode = getVerifyFormCode({
      eventCode: EVENT_CODE.ON_ROW_EDIT,
      row,
      actions: actionsForConfig,
    })

    const formTitle = getVerifyFormTitle({
      eventCode: EVENT_CODE.ON_ROW_EDIT,
      actions: actionsForConfig,
    })

    if (formCode && entityCode) {
      setPrevSelectedRowId(
        row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
      )

      if (links) {
        if (getLeaveForm?.()) {
          return
        }
        onResetEditForm?.()

        const id = uuid()

        setHistoryFormInForm({ formCode, newId: id, objectId: row._id, event: FORM_TYPE.EDIT })

        return
      }

      resetDynamicAndStaticForms()

      const globalId = uuid()

      setHistoryFormByView({ formCode, newId: globalId, objectId: row._id, event: FORM_TYPE.EDIT })

      handleOpenForm?.(
        {
          event: FORM_TYPE.EDIT,
          formCode,
          objectCode,
          objectId: row._id,
          viewCode: entityCode,
          path,
          search,
          id: globalId,
          entityType: type,
          viewId: entityId || undefined,
          title: formTitle,
        },
        event,
        CLICK_EVENT_TYPE.EDIT_CLICK
      )
    }
  }

  const handleCloseDeleteModal = () => {
    setIsShowDeleteModal(false)
  }

  const handleCloseUserRestrictionAsDefaultModel = () => {
    setIsShowUserRestrictionAsDefaultModel(false)
  }

  const handleShowUserRestrictionAsDefaultModel = () => {
    setIsShowUserRestrictionAsDefaultModel(true)
  }

  const handleClickDelete = (
    params?: GridCellParams<ObjectDataRecord>,
    rowDeleteButtonClickAction?: ActionRow
  ) => {
    const command = rowDeleteButtonClickAction?.command
    const srcObj = params?.row ?? {}

    if (!isShowDeleteModal) {
      const deleteModalData = command
        ? {
            method: command?.httpMethod ?? 'GET',
            objectCode: command?.objectCode ?? '',
            commandCode: command?.name ?? '',
            params: getCommandRequestData(command?.parameters ?? [], srcObj),
            body: getCommandRequestData(command?.body ?? []),
          }
        : {
            objectId: params?.row._id,
            viewId: entityId || undefined,
          }

      setDeleteModalData(deleteModalData)
      setIsShowDeleteModal(true)

      return
    }

    if (deleteModalData) {
      if (deleteModalData.commandCode) {
        executeCommand(deleteModalData)
      } else {
        deleteObject(deleteModalData)
      }

      handleCloseDeleteModal()
    }
  }

  const handleClosePreviewFileModal = () => {
    setShowPreviewFileModal(false)
  }

  const handlePreviewButtonClick = async (
    params: GridCellParams<any, unknown, unknown, GridTreeNode>,
    event: MouseEvent<HTMLButtonElement>,
    rowPreviewButtonClickAction: ActionRow
  ) => {
    const command = rowPreviewButtonClickAction?.command
    const srcObj = params?.row ?? {}

    if (command) {
      const data = (await executeCommand({
        method: command?.httpMethod ?? 'GET',
        objectCode: command?.objectCode ?? '',
        commandCode: command?.name ?? '',
        params: getCommandRequestData(command?.parameters ?? [], srcObj),
        body: getCommandRequestData(command?.body ?? []),
      }).unwrap()) as { download_link: string }

      const urlPath = extractUrlPath(data.download_link)

      setPreviewFileUrl(process.env.NODE_ENV === 'development' ? `/api/files${urlPath}` : urlPath)
    }

    setShowPreviewFileModal(true)
  }

  const handleDownloadButtonClick = async (
    params: GridCellParams<any, unknown, unknown, GridTreeNode>,
    event: MouseEvent<HTMLButtonElement>,
    rowDownloadButtonClickAction: ActionRow
  ) => {
    const command = rowDownloadButtonClickAction.command
    const srcObj = params?.row ?? {}

    if (rowDownloadButtonClickAction.command) {
      setActiveDownloadRows(prev => ({ ...prev, [params.row.id]: true }))

      const file = await executeCommand({
        method: command?.httpMethod ?? 'GET',
        objectCode: command?.objectCode ?? '',
        commandCode: command?.name ?? '',
        params: getCommandRequestData(command?.parameters ?? [], srcObj),
        body: getCommandRequestData(command?.body ?? []),
      })
        .unwrap()
        .finally(() => setActiveDownloadRows(prev => ({ ...prev, [params.row.id]: false })))

      const fileLink = !isEmpty(file) && isObject(file) && Object.values(file)[0]
      const fileName = getFileNameAndExtensionFromURL(fileLink as URL)

      if (fileLink) {
        const a = document.createElement('a')
        a.href = fileLink as string
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    }
  }

  const handleAdditionalButtonClick = (
    params: GridCellParams<any, ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    const initialPosition = getInitialPositionWindow(event)
    const viewId = entityId
    const viewCode = entityCode

    const row = params.row
    const srcObj = row ?? {}

    const additionalButtonAction = findActionByEventCode(
      EVENT_CODE.ON_ROW_ADDITIONAL_BUTTON_CLICK,
      actions
    )
    const command = additionalButtonAction?.command

    if (command) {
      executeCommand({
        method: command.httpMethod ?? 'GET',
        objectCode: command.objectCode ?? '',
        commandCode: command.name ?? '',
        params: getCommandRequestData(command.parameters ?? [], srcObj),
        body: getCommandRequestData(command.body ?? [], srcObj),
      })

      return
    }

    const formCode = getVerifyFormCode({
      eventCode: EVENT_CODE.ON_ROW_ADDITIONAL_BUTTON_CLICK,
      row,
      actions: actionsForConfig,
    })

    // if (checkMaxOpenedDialogs()) return

    if (viewId && formCode) {
      const id = uuid()
      setPrevSelectedRowId(
        row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
      )
      dispatch(
        openFormDialog({
          id,
          parentDialogId: null,
          type: DIALOG_WINDOW_TYPE.FORM,
          // title: option?.elementTitle,
          title: null,
          meta: {
            preFill: {
              objectCode,
              // TODO Сделать нормальный ид через флаги
              objectId: row._id,
            },
            objectCode: '',
            // formCode: option.elementCode.toString(),
            formCode,
            viewCode: '',
            // path: option.id.toString(),
            path,
            event: FORM_TYPE.CREATE,
            globalId: id,
            entityType: ENTITY.VIEW,
            // viewId: option.elementId,
            viewId,
            isIndependentForm: true,
          },
          initialPosition,
        })
      )

      dispatch(
        addFormHistory({
          id,
          form: {
            // formCode: option.elementCode.toString(),
            formCode,
            objectCode: '',
            event: FORM_TYPE.CREATE,
            objectId: null,
            // title: option?.elementTitle || '',
            title: '',
            id,
            isWindow: true,
          },
        })
      )
    }
    // TODO Открытие формы
  }

  const handleRowButtonClick = (
    params: GridCellParams<any, ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    const row = params.row
    const formCode = getVerifyFormCode({
      eventCode: EVENT_CODE.ON_ROW_CLICK,
      row,
      actions: actionsForConfig,
    })
    const formTitle = getVerifyFormTitle({
      eventCode: EVENT_CODE.ON_ROW_CLICK,
      actions: actionsForConfig,
    })

    const globalId = uuid()

    if (formCode && entityCode) {
      setPrevSelectedRowId(
        row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
      )
      handleClearSamePageForms()

      setHistoryFormByView({
        formCode,
        newId: globalId,
        objectId: row._id,
        event: FORM_TYPE.VIEW,
        isWindow: true,
      })

      handleOpenForm?.(
        {
          event: FORM_TYPE.VIEW,
          formCode,
          objectCode,
          objectId: row._id,
          viewCode: entityCode,
          path,
          search,
          id: globalId,
          entityType: type,
          viewId: entityId || undefined,
          title: formTitle,
        },
        event,
        CLICK_EVENT_TYPE.ROW_CLICK
      )
    }
  }

  const handleRowClick = (params: GridRowParams<ObjectDataRecord>, event: MuiEvent<MouseEvent>) => {
    if (readonly || event.ctrlKey) {
      return
    }

    onRowClick?.(params, event)

    const row = params.row

    const formCode = getVerifyFormCode({
      eventCode: EVENT_CODE.ON_ROW_CLICK,
      row,
      actions: actionsForConfig,
    })

    const formTitle = getVerifyFormTitle({
      eventCode: EVENT_CODE.ON_ROW_CLICK,
      actions: actionsForConfig,
    })

    const globalId = uuid()

    if (type === ENTITY.DROP_DOWN_ENTITY) {
      if (onMultiSelectPickerOption && selectedRows && objectValueNameOption) {
        handleSetSelectedRows(row)

        const newRows = getUpdatedOptions(selectedRows, row, ({ _id }) => _id === row._id)
        // ВЫЗЫВАЕТСЯ В OBJECT PICKER
        onMultiSelectPickerOption?.(newRows)

        return
      }

      handleSetSelectedRows(row)
      // ВЫЗЫВАЕТСЯ В OBJECT PICKER
      onSelectPickerOption?.(params.row)

      return
    }

    handleSetSelectedRows(row)
    handleChangeHighlightedRows(row)

    if (formCode && entityCode) {
      if (event?.type === CLICK_TYPE.SINGLE) {
        // TODO на рефакторинге заменить e.ctrlKey && e.shiftKey на event.key из констант hotkey.ts
        if (event.ctrlKey && event.shiftKey) {
          return
        }

        if (event.shiftKey) {
          const openedDialogByRowId = dialogWindows.find(
            dialog => dialog.meta?.objectId === row._id && formCode === dialog.meta?.formCode
          )

          if (openedDialogByRowId) {
            dispatch(
              setSelectedDialog({
                id: openedDialogByRowId[
                  CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME
                ],
              })
            )

            return
          }

          // При открытии из вложенной вьюхи в форму
          if (links) {
            setHistoryFormByForm({
              formCode,
              newId: globalId,
              objectId: row._id,
              event: FORM_TYPE.VIEW,
              isWindow: true,
            })

            handleOpenForm?.(
              {
                event: FORM_TYPE.VIEW,
                formCode,
                objectCode,
                objectId: row._id,
                viewCode: entityCode,
                path,
                search,
                id: globalId,
                entityType: type,
                viewId: entityId || undefined,
                title: formTitle,
              },
              event,
              CLICK_EVENT_TYPE.ROW_CLICK
            )

            return
          }

          handleOpenForm?.(
            {
              event: FORM_TYPE.VIEW,
              formCode,
              objectCode,
              objectId: row._id,
              viewCode: entityCode,
              path,
              search,
              id: globalId,
              entityType: type,
              viewId: entityId || undefined,
              title: formTitle,
            },
            event,
            CLICK_EVENT_TYPE.ROW_CLICK
          )

          setPrevSelectedRowId(
            row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
          )

          setHistoryFormByView({
            formCode,
            newId: globalId,
            objectId: row._id,
            event: FORM_TYPE.VIEW,
            isWindow: true,
          })

          return
        }

        // Убрана возможность открывать форму вью по ctrl + click. https://jiradc.utg.group/browse/WF-3103

        // if (event.ctrlKey || event.shiftKey) {
        //   // Нельзя открыть окно с формой, если вью находится в другой форме
        //   if (isNestedView) {
        //     return
        //   }

        //   handleAddSamePageForm({
        //     rowId: row.id,
        //     formCode,
        //     objectCode,
        //     objectId: row._id,
        //     viewCode: entityCode,
        //     viewId: entity?.id as string,
        //     path,
        //     event: FORM_TYPE.VIEW,
        //     variant: event.ctrlKey
        //       ? PASSED_FORM_DATA_VARIANT.STATIC
        //       : PASSED_FORM_DATA_VARIANT.DYNAMIC,
        //     globalId,
        //     openedContainers: [],
        //   })

        //   if (event.ctrlKey) {
        //     isShiftHeld && handleSetIsShiftHeld(false)
        //     handleSetIsCtrlHeld(true)
        //   }

        //   if (event.shiftKey) {
        //     isCtrlHeld && handleSetIsCtrlHeld(false)
        //     handleSetIsShiftHeld(true)
        //   }

        //   return
        // }

        // if (isCtrlHeld) {
        //   handleAddSamePageForm(
        //     {
        //       rowId: row.id,
        //       formCode,
        //       objectCode,
        //       objectId: row._id,
        //       viewCode: entityCode,
        //       viewId: entity?.id as string,
        //       path,
        //       event: FORM_TYPE.VIEW,
        //       variant: PASSED_FORM_DATA_VARIANT.STATIC,
        //       globalId,
        //       openedContainers: [],
        //     },
        //     true
        //   )
        // }
      } else if (event?.type === CLICK_TYPE.DOUBLE) {
        handleClearSamePageForms()
        setPrevSelectedRowId(
          row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
        )
        // для вьюхи вложенной в форму
        if (links) {
          if (getLeaveForm?.()) {
            return
          }

          onResetEditForm?.()

          setHistoryFormInForm({
            formCode,
            newId: globalId,
            objectId: row._id,
            event: FORM_TYPE.VIEW,
          })

          return
        }

        resetDynamicAndStaticForms()

        setHistoryFormByView({
          formCode,
          newId: globalId,
          objectId: row._id,
          event: FORM_TYPE.VIEW,
        })

        handleOpenForm?.(
          {
            event: FORM_TYPE.VIEW,
            formCode,
            objectCode,
            objectId: row._id,
            viewCode: entityCode,
            path,
            search,
            id: globalId,
            entityType: type,
            viewId: entityId || undefined,
            title: formTitle,
          },
          event,
          CLICK_EVENT_TYPE.ROW_CLICK
        )
      }
    }
  }

  const handleAddEmptyRow = () => {
    const id = uuid()
    const emptyRow: ObjectDataRecord = {
      [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id,
      isNew: true,
      isDirty: false,
    }

    if (prefillData && Object.keys(prefillData).length) {
      const rows: Record<string, any> = {}
      const labels: Record<string, string> = {}

      Object.entries(prefillData).forEach(([key, value]) => {
        if (key.includes('.')) {
          const fieldName = key.split('.')[0]
          labels[fieldName] = value === 0 ? String(value) : value
        } else {
          rows[key] = value
        }
      })

      unsavedChangesRef.current = {
        ...unsavedChangesRef.current,
        rows: {
          ...unsavedChangesRef.current.rows,
          [id]: {
            ...emptyRow,
            ...rows,
          },
        },
        labels: {
          ...unsavedChangesRef.current.labels,
          [id]: labels,
        },
      }

      const rowWithPrefill = { ...emptyRow, ...rows }

      setNewRows(prev => [rowWithPrefill, ...prev])
    } else {
      unsavedChangesRef.current = {
        ...unsavedChangesRef.current,
        rows: {
          ...unsavedChangesRef.current.rows,
          [id]: emptyRow,
        },
      }
      setNewRows(prev => [emptyRow, ...prev])
    }
  }

  const handlePendingRefresh = () => {
    if (hasPendingRefresh) {
      setHasPendingRefresh(false)
      setIsUpdateLoading(true)
      refreshAllPages()
        .finally(() => setIsUpdateLoading(false))
        .catch(console.error)
    }
  }

  const handleChangeHighlightedRows = (row: ObjectDataRecord) => {
    const rowId = row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]

    if (highlightedSelectedRowsIds?.some(selectedRowId => selectedRowId === rowId)) {
      setHighlightedSelectedRows(
        highlightedSelectedRowsIds.filter(selectedRowId => selectedRowId !== rowId)
      )

      return
    }

    setHighlightedSelectedRows([row])
  }

  const handleCellEditStop = useCallback(
    (params: GridCellEditStopParams, event: MuiEvent) => {
      if (isDropDownWindowOpen) {
        event.defaultMuiPrevented = true

        return
      }

      handlePendingRefresh()
    },
    [isDropDownWindowOpen]
  )

  const handleCellDoubleClick = (
    params: GridRowParams<ObjectDataRecord>,
    event: MuiEvent<MouseEvent>
  ) => {
    event.defaultMuiPrevented = true
    const isCheckbox =
      params.colDef.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      params.colDef.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

    if (params.row.isNew || isEditMode || params.cellMode === GRID_CELL_MODE.EDIT) {
      event.stopPropagation()
    }

    if (
      params.cellMode === GRID_CELL_MODE.VIEW &&
      params.isEditable &&
      !isCheckbox &&
      (params.row.isNew || isEditMode)
    ) {
      apiRef.current.startCellEditMode({ id: params.id, field: params.field })
      event.stopPropagation()
    }
  }

  const handleCellKeyDown = (params, event) => {
    const isCheckbox =
      params.colDef.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      params.colDef.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

    if (event.key === HOT_KEY.ESCAPE) {
      event.defaultMuiPrevented = true
      event.stopPropagation()
      handleDiscardViewEditChanges(hasUnsavedChanges)

      return
    }

    if (
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Shift'
    ) {
      return
    }

    if (event.key === 'Tab') {
      event.preventDefault()

      const cell = params
      const api = apiRef.current
      const visibleColumns = api.getVisibleColumns()
      const currentColIndex = visibleColumns.findIndex(col => col.field === cell.field)

      if (currentColIndex === -1) return

      const sortedRowIds = api.getSortedRowIds()
      const currentRowIndex = sortedRowIds.indexOf(cell.id)

      if (currentRowIndex === -1) return

      let nextRowId = cell.id
      let nextField = null

      if (event.shiftKey) {
        event.defaultMuiPrevented = true
        if (currentColIndex > 0) {
          nextField = visibleColumns[currentColIndex - 1].field
        } else {
          const prevRowIndex = currentRowIndex - 1

          if (prevRowIndex >= 0) {
            nextRowId = sortedRowIds[prevRowIndex]
            nextField = visibleColumns[visibleColumns.length - 1].field
          } else {
            const lastRowId = sortedRowIds[sortedRowIds.length - 1]
            if (lastRowId !== undefined) {
              nextRowId = lastRowId
              nextField = visibleColumns[visibleColumns.length - 1].field
            }
          }
        }
      } else {
        if (currentColIndex < visibleColumns.length - 1) {
          nextField = visibleColumns[currentColIndex + 1].field
        } else {
          const nextRowIndex = currentRowIndex + 1

          if (nextRowIndex < sortedRowIds.length) {
            nextRowId = sortedRowIds[nextRowIndex]
            nextField = visibleColumns[0].field
          } else {
            const firstRowId = sortedRowIds[0]
            if (firstRowId !== undefined) {
              nextRowId = firstRowId
              nextField = visibleColumns[0].field
            }
          }
        }
      }

      if (nextField) {
        api.setCellFocus(nextRowId, nextField)

        const rowIndex = api.getRowIndexRelativeToVisibleRows(nextRowId)
        const colIndex = visibleColumns.findIndex(col => col.field === nextField)

        if (rowIndex !== -1 && colIndex !== -1) {
          api.scrollToIndexes({
            rowIndex,
            colIndex,
          })
        }
      }

      return
    }

    if (
      params.cellMode === 'view' &&
      params.isEditable &&
      !isCheckbox &&
      (params.row.isNew || isEditMode)
    ) {
      apiRef.current.startCellEditMode({ id: params.id, field: params.field })
      event.stopPropagation()
    }

    if (!event.ctrlKey && event.key !== HOT_KEY.ENTER) {
      event.stopPropagation()
    }

    if (event.key === HOT_KEY.ENTER) {
      event.defaultMuiPrevented = true
      event.stopPropagation()

      const field = params.field
      const isViewMode = params.cellMode === 'view'

      if (isViewMode && isCheckbox && (isEditMode || (hasNewRows && params.row.isNew))) {
        const newValue = toggleCheckboxValue(params)

        const oldRow = params.row
        const newRow = { ...params.row, [field]: newValue }

        apiRef.current.updateRows([
          {
            [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]:
              params.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME],
            [field]: newValue,
          },
        ])

        handleViewRowUpdate(newRow, oldRow, { rowId: params.row._id })
      }
    }

    if (!isEditMode) {
      event.defaultMuiPrevented = true
    }
  }

  const handleCellClick = async (cell: GridCellParams, event: MuiEvent<MouseEvent>) => {
    const rowId =
      cell.row._id ?? cell.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
    const field = cell.field
    const isCheckbox =
      cell.colDef?.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
      cell.colDef?.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN
    const isViewMode = cell.cellMode === 'view'

    if (isCtrlPressed && !isEditMode && !hasNewRows && isViewMode) {
      if (isCheckbox) {
        const newValue = toggleCheckboxValue(cell)

        updateViewCellField({
          viewId: entity?.id ?? '',
          record: {
            [cell.field]: newValue,
            _id: cell.row._id,
          },
        })
          .unwrap()
          .then(response => {
            if (response[0][field] !== newValue) {
              setObjectData(prev => {
                const copy = [...prev]
                const index = copy.findIndex(row => row._id === rowId)
                copy[index][field] = cell.value

                return copy
              })
            } else {
              setObjectData(prev => {
                const copy = [...prev]
                const index = copy.findIndex(row => row._id === rowId)
                copy[index][field] = newValue

                return copy
              })
            }
          })
          .catch(err => {
            setObjectData(prev => {
              const copy = [...prev]
              const index = copy.findIndex(row => row._id === rowId)
              copy[index][field] = cell.value

              return copy
            })
          })

        return
      }

      setSelectedRows([cell.row])

      apiRef.current.startCellEditMode({
        id: cell.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME],
        field: cell.field,
      })
    }

    if (isViewMode && isCheckbox && (isEditMode || (hasNewRows && cell.row.isNew))) {
      const newValue = toggleCheckboxValue(cell)

      const oldRow = cell.row
      const newRow = { ...cell.row, [cell.field]: newValue }

      apiRef.current.updateRows([
        {
          [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]:
            cell.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME],
          [field]: newValue,
        },
      ])

      handleViewRowUpdate(newRow, oldRow, { rowId })
    }

    const isLinkToFormCell = cell.colDef.type === 'custom' && cell.colDef.field === 'linkToForm'

    if (!isLinkToFormCell) {
      return
    }

    event.stopPropagation()

    const row = cell.row

    const formCode = getVerifyFormCode({
      eventCode: EVENT_CODE.ON_ROW_CLICK,
      row,
      actions: actionsForConfig,
    })

    const formTitle = getVerifyFormTitle({
      eventCode: EVENT_CODE.ON_ROW_CLICK,
      actions: actionsForConfig,
    })

    const globalId = uuid()

    setPrevSelectedRowId(
      row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
    )
    handleClearSamePageForms()
    // для вьюхи вложенной в форму

    if (formCode && entityCode) {
      if (event.shiftKey) {
        const openedDialogByRowId = dialogWindows.find(
          dialog => dialog.meta?.objectId === row._id && formCode === dialog.meta?.formCode
        )

        if (openedDialogByRowId) {
          dispatch(
            setSelectedDialog({
              id: openedDialogByRowId[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME],
            })
          )

          return
        }

        // Открытие через shift в если вьюха вложеная в форму
        if (links) {
          if (getLeaveForm?.()) {
            return
          }

          onResetEditForm?.()

          setHistoryFormByForm({
            formCode,
            newId: globalId,
            objectId: row._id,
            event: FORM_TYPE.VIEW,
          })

          handleOpenForm?.(
            {
              event: FORM_TYPE.VIEW,
              formCode,
              objectCode,
              objectId: row._id,
              viewCode: entityCode,
              path,
              search,
              id: globalId,
              entityType: type,
              viewId: entityId || undefined,
              title: formTitle,
            },
            event,
            CLICK_EVENT_TYPE.ROW_CLICK
          )

          return
        }

        setHistoryFormByView({
          formCode,
          newId: globalId,
          objectId: row._id,
          event: FORM_TYPE.VIEW,
          isWindow: true,
        })

        handleOpenForm?.(
          {
            event: FORM_TYPE.VIEW,
            formCode,
            objectCode,
            objectId: row._id,
            viewCode: entityCode,
            path,
            search,
            id: globalId,
            entityType: type,
            viewId: entityId || undefined,
            title: formTitle,
          },
          event,
          CLICK_EVENT_TYPE.ROW_CLICK
        )

        return
      }

      resetDynamicAndStaticForms()

      // открытие если вьюха вложена в форму
      if (links) {
        if (getLeaveForm?.()) {
          return
        }

        onResetEditForm?.()

        setHistoryFormInForm({
          formCode,
          newId: globalId,
          objectId: row._id,
          event: FORM_TYPE.VIEW,
        })

        return
      }

      // дефолтное открытие при клике на иконку
      setHistoryFormByView({ formCode, newId: globalId, objectId: row._id, event: FORM_TYPE.VIEW })

      handleOpenForm?.(
        {
          event: FORM_TYPE.VIEW,
          formCode,
          objectCode,
          objectId: row._id,
          viewCode: entityCode,
          path,
          search,
          id: globalId,
          entityType: type,
          viewId: entityId || undefined,
          title: formTitle,
        },
        event,
        CLICK_EVENT_TYPE.CELL_CLICK
      )
    }
  }

  const handleCreate = (event: MouseEvent<HTMLButtonElement>) => {
    const formCode = getVerifyFormCode({
      eventCode: EVENT_CODE.ON_ROW_CREATE,
      actions: actionsForConfig,
    })

    const formTitle = getVerifyFormTitle({
      eventCode: EVENT_CODE.ON_ROW_CREATE,
      actions: actionsForConfig,
    })

    const globalId = uuid()

    event.stopPropagation()

    if (formCode && entityCode) {
      if (links) {
        if (getLeaveForm?.()) {
          return
        }
        onResetEditForm?.()

        setHistoryFormByForm({
          formCode,
          newId: globalId,
          objectId: null,
          event: FORM_TYPE.CREATE,
          isWindow: true,
        })
        // Форма создания из list control открывается в новом блокирующем окне
        handleOpenForm?.(
          {
            event: FORM_TYPE.CREATE,
            formCode,
            objectCode,
            viewCode: entityCode,
            path,
            search,
            id: globalId,
            entityType: type,
            viewId: entityId || undefined,
            title: formTitle,
            preFill: prefillCreateObjectRecordInForm,
            variables: outerVariables,
          },
          event,
          CLICK_EVENT_TYPE.CREATE_CLICK
        )

        return
      }
      resetDynamicAndStaticForms()

      setHistoryFormByView({ formCode, newId: globalId, objectId: null, event: FORM_TYPE.CREATE })

      handleOpenForm?.(
        {
          event: FORM_TYPE.CREATE,
          formCode,
          objectCode,
          viewCode: entityCode,
          path,
          search,
          id: globalId,
          entityType: type,
          viewId: entityId || undefined,
          title: formTitle,
          preFill: prefillCreateObjectRecordInForm,
          variables: outerVariables,
        },
        event,
        CLICK_EVENT_TYPE.CREATE_CLICK
      )
    }
  }

  const stopAllCellsEditMode = () => {
    const editRows = apiRef.current.state.editRows

    Object.entries(editRows).forEach(([rowId, fields]) => {
      Object.keys(fields).forEach(field => {
        apiRef.current.stopCellEditMode({ id: rowId, field })
      })
    })
  }

  const handleViewRowUpdate = useCallback<NonNullable<DataGridProps['processRowUpdate']>>(
    (newRow, oldRow) => {
      handlePendingRefresh()

      const rowId =
        newRow._id ?? newRow[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
      const {
        [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id,
        isNew,
        isDirty,
        ...fieldsToCheck
      } = newRow

      const allFieldsEmpty = Object.values(fieldsToCheck).every(value => isEmptyValue(value))

      //Удаляем ключи филдов чтобы они не учитывались при валидации в случае если строка была изменена и потом очищена
      if (allFieldsEmpty && unsavedChangesRef.current.rows[rowId]) {
        const newRows = { ...unsavedChangesRef.current.rows }
        newRows[rowId] = {
          [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id,
          isNew,
          isDirty: false,
        }
        unsavedChangesRef.current.rows = newRows
        delete unsavedChangesRef.current.labels[rowId]

        const cleanRow = {
          [CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]: id,
          isNew: true,
          isDirty: false,
        }
        setNewRows(prev =>
          prev.map(row =>
            (row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME] ?? row._id) === rowId
              ? cleanRow
              : row
          )
        )

        setErrorFields(prev => {
          const updated = { ...prev }
          Object.keys(updated).forEach(key => {
            if (key.startsWith(`${rowId}.`)) {
              delete updated[key]
            }
          })

          return updated
        })

        return newRow
      }

      const requiredFields = columns.filter(col => col.required).map(col => col.field)
      const originalRow = unsavedChangesRef.current.originalRows[rowId] || oldRow

      const changedFields = Object.keys(fieldsToCheck).filter(field => {
        const cellField = columns.find(col => col.field === field)
        const isMultiple = cellField?.fieldType === OBJECT_FIELD_TYPE.ARRAY

        if (isMultiple) {
          const originalValue = Array.isArray(originalRow[field])
            ? originalRow[field]
            : objectToOptions(originalRow[field])

          const newValue = Array.isArray(newRow[field])
            ? newRow[field]
            : objectToOptions(newRow[field])

          return !isEqual(newValue, originalValue)
        } else {
          return (
            (newRow[field]?.id || newRow[field]) !== (originalRow[field]?.id || originalRow[field])
          )
        }
      })

      if (changedFields.length > 0) {
        // Если единичное редактирвание по Ctrl + click сразу отправляем запрос
        if (!isEditMode && !hasNewRows) {
          const field = changedFields[0]
          const cellField = columns.find(col => col.field === field)
          const isMultiple = cellField?.fieldType === OBJECT_FIELD_TYPE.ARRAY
          const newValue = newRow[field]

          if (cellField?.required && !isNotEmpty(newValue)) {
            return oldRow
          }

          const payloadValue = isMultiple
            ? newValue?.map(option => option.id ?? option.internalId) ?? []
            : newValue?.id ?? newValue?.internalId ?? newValue
          const originalValue = originalRow[field]

          setObjectData(prev => {
            const copy = [...prev]
            const index = copy.findIndex(row => row._id === rowId)
            copy[index][field] = newValue

            return copy
          })

          updateViewCellField({
            viewId: entity?.id ?? '',
            record: {
              [field]: payloadValue,
              _id: rowId,
            },
          })
            .unwrap()
            .then(([response]) => {
              const isResponseValueMatch = checkIsResponseValueMatch(
                response[field],
                payloadValue,
                field,
                columns
              )

              if (!isResponseValueMatch) {
                setObjectData(prev => {
                  const copy = [...prev]
                  const index = copy.findIndex(row => row._id === rowId)
                  copy[index][field] = originalValue

                  return copy
                })
              }
            })
            .catch(() => {
              setObjectData(prev => {
                const copy = [...prev]
                const index = copy.findIndex(row => row._id === rowId)
                copy[index][field] = originalValue

                return copy
              })
            })
            .finally(() => clearAllUnsavedChanges())

          return newRow
        }

        if (!newRow.isNew && !unsavedChangesRef.current.originalRows[rowId]) {
          unsavedChangesRef.current.originalRows[rowId] = oldRow
        }

        if (!newRow.isNew) {
          unsavedChangesRef.current.rows[rowId] = {}
        }

        Object.keys(missingRequiredFields.current).forEach(field => {
          if (missingRequiredFields.current[field] === rowId) {
            delete missingRequiredFields.current[field]
          }
        })

        changedFields.forEach(field => {
          const newValue = newRow[field]
          const cellField = columns.find(col => col.field === field)
          const isMultiple = cellField?.fieldType === OBJECT_FIELD_TYPE.ARRAY
          const isRefField =
            cellField?.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK ||
            cellField?.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK
          const errorKey = `${rowId}.${field}`

          unsavedChangesRef.current.rows[rowId][field] = isMultiple
            ? newValue?.map(option => option.id ?? option.internalId) ?? []
            : newValue?.id ?? newValue?.internalId ?? newValue

          unsavedChangesRef.current.rows[rowId].isDirty = true

          if (isObject(newValue) || isRefField) {
            if (!unsavedChangesRef.current.labels[rowId]) {
              unsavedChangesRef.current.labels[rowId] = {}
            }

            if (isMultiple) {
              unsavedChangesRef.current.labels[rowId][field] = newValue || null
            } else {
              unsavedChangesRef.current.labels[rowId][field] = newValue?.label || null
            }
          }

          newRow.isDirty = true

          setErrorFields(prev => {
            const updated = { ...prev }
            Object.keys(updated).forEach(key => {
              if (key === errorKey) {
                delete updated[key]
              }
            })

            return updated
          })

          delete missingRequiredFields.current[field]

          if (requiredFields.includes(field) && !isNotEmpty(newValue)) {
            setErrorFields(prev => ({ ...prev, [errorKey]: true }))
            missingRequiredFields.current[field] = rowId
            if (unsavedChangesRef.current.labels[rowId]?.[field]) {
              delete unsavedChangesRef.current.labels[rowId][field]
            }
          }

          if (!requiredFields.includes(field) && newRow.isNew && isEmptyValue(newValue)) {
            delete unsavedChangesRef.current.rows[rowId][field]

            setNewRows(prev =>
              prev.map(row => {
                const currentRowId =
                  row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
                if (currentRowId === rowId) {
                  const updatedRow = { ...row }
                  delete updatedRow[field]

                  return updatedRow
                }

                return row
              })
            )

            if (unsavedChangesRef.current.labels[rowId]?.[field]) {
              delete unsavedChangesRef.current.labels[rowId][field]
            }
          }

          if (cellField?.valueType === 'integer' && !isObject(newValue)) {
            const num = Number(newValue)
            const isValidInteger = Number.isInteger(num)

            if (!isValidInteger) {
              const errorKey = `${rowId}.${field}`
              setErrorFields(prev => ({ ...prev, [errorKey]: true }))
            }
          }

          if (newValue && cellField?.valueType === 'datetime') {
            const year = newValue.slice(0, 4)
            const isValidYear = year > 1900 && year < 2100
            if (!isValidYear) {
              const errorKey = `${rowId}.${field}`
              setErrorFields(prev => ({ ...prev, [errorKey]: true }))
            }
          }
        })
        handleSetHasUnsavedChanges(true)

        if (dialogId) {
          dispatch(setDialogDirty({ id: dialogId, isDirty: true }))
        }

        requiredFields.forEach(field => {
          const errorKey = `${rowId}.${field}`
          const newValue = newRow[field]
          const originalValue = originalRow[field]

          const isSameAsOriginal = (newValue?._id ?? newValue) === originalValue

          if (!newRow.isNew) {
            if (isNotEmpty(newValue) || isSameAsOriginal) {
              setErrorFields(prev => {
                const updated = { ...prev }
                delete updated[errorKey]

                return updated
              })

              delete missingRequiredFields.current[field]
            }
          }
        })

        return newRow
      }

      if (!changedFields.length && !newRow.isNew) {
        delete unsavedChangesRef.current.rows[rowId]
        delete unsavedChangesRef.current.originalRows[rowId]
        delete unsavedChangesRef.current.labels[rowId]

        setErrorFields(prev => {
          const updated = { ...prev }
          Object.keys(updated).forEach(key => {
            if (key.startsWith(`${rowId}.`)) {
              delete updated[key]
            }
          })

          return updated
        })

        if (!Object.keys(unsavedChangesRef.current.rows).length) {
          handleSetHasUnsavedChanges(false)
        }
      }

      return newRow
    },
    [dialogId, columns, hasPendingRefresh]
  )

  const clearAllUnsavedChanges = () => {
    if (dialogId) {
      dispatch(setDialogDirty({ id: dialogId, isDirty: false }))
    }

    unsavedChangesRef.current = {
      labels: {},
      rows: {},
      originalRows: {},
    }

    missingRequiredFields.current = {}

    setNewRows([])
    setErrorFields({})
    setIsEditMode(false)
    handleSetHasUnsavedChanges(false)
    stopAllCellsEditMode()
    setSelectedRows([])
  }

  const handleDiscardViewEditChanges = useCallback(
    (isDirty?: boolean) => {
      if (isDirty && !confirm(t('notifications.leave'))) {
        return
      }

      Object.values(unsavedChangesRef.current.originalRows).forEach(row => {
        apiRef.current.updateRows([row])
      })

      clearAllUnsavedChanges()
    },
    [apiRef]
  )

  const handleSaveViewEditChanges = useCallback(
    async (hasNewRows?: boolean) => {
      const requiredFields = columns.filter(col => col.required).map(col => col.field)

      let newErrorFields: Record<string, boolean> = {}

      if (hasNewRows) {
        const newRows = Object.values(unsavedChangesRef.current.rows).filter(row => row.isNew)
        newErrorFields = validateNewRows(newRows, requiredFields)
      }

      if (Object.keys(newErrorFields).length > 0) {
        setErrorFields(newErrorFields)

        showSnackbarNotification({
          text: t('error.requiredFieldsMissing', {
            fields: Object.keys(newErrorFields)
              .map(key => key.split('.')[1])
              .join(', '),
          }) as string,
          options: {
            variant: 'error',
          },
        })

        return
      }

      if (hasExternalActionData) {
        setExternalActionData(prev => {
          const copy = [...prev]

          const unsavedRows = Object.entries(unsavedChangesRef.current.rows)

          unsavedRows.forEach(([rowId, changedFields]) => {
            const index = copy.findIndex(
              row => row._id || row.CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID === rowId
            )

            const labels = unsavedChangesRef.current.labels[rowId] ?? {}

            const updatedRow = {
              ...(index !== -1 ? copy[index] : {}),
              ...changedFields,
              ...labels,
            }

            if (index !== -1) {
              copy[index] = updatedRow
            }
          })

          clearAllUnsavedChanges()

          return copy
        })

        return
      }

      setObjectData(prev => {
        const copy = [...prev]

        const unsavedRows = Object.entries(unsavedChangesRef.current.rows)

        unsavedRows.forEach(([rowId, changedFields]) => {
          const index = copy.findIndex(row => row._id === rowId)

          const labels = unsavedChangesRef.current.labels[rowId] ?? {}

          const updatedRow = {
            ...(index !== -1 ? copy[index] : {}),
            ...changedFields,
            ...labels,
          }

          if (index !== -1) {
            copy[index] = updatedRow
          }
        })

        return copy
      })

      Object.entries(unsavedChangesRef.current.rows).map(([rowId, changedFields]) => {
        const originalData = unsavedChangesRef.current.originalRows[rowId]
        const labels = unsavedChangesRef.current.labels[rowId] ?? {}

        if (changedFields.isNew) {
          if (changedFields.isDirty) {
            createViewCellField({
              viewId: entity?.id ?? '',
              record: changedFields,
            })
          }
        } else {
          const updatedRowData = { _id: rowId, ...changedFields }
          updateViewCellField({
            viewId: entity?.id ?? '',
            record: updatedRowData,
          })
            .unwrap()
            .then(([response]) => {
              const unchangedValues: Record<string, any> = {}

              Object.keys(changedFields).forEach((field: string) => {
                const isResponseValueMatch = checkIsResponseValueMatch(
                  response[field],
                  changedFields[field],
                  field,
                  columns
                )

                if (!isResponseValueMatch) {
                  unchangedValues[field] = originalData[field]
                }
              })

              if (Object.keys(unchangedValues).length) {
                setObjectData(prev => {
                  const copy = [...prev]
                  const index = copy.findIndex(row => row._id === rowId)

                  const updatedRow = {
                    ...(index !== -1 ? copy[index] : {}),
                    ...labels,
                    ...unchangedValues,
                  }

                  if (index !== -1) {
                    copy[index] = updatedRow
                  }

                  return copy
                })
              }
            })
            .catch(() => {
              setObjectData(prev => {
                const copy = [...prev]
                if (originalData) {
                  const index = copy.findIndex(row => row._id === rowId)
                  if (index !== -1) {
                    copy[index] = originalData
                  }
                }

                return copy
              })
            })
        }
      })

      clearAllUnsavedChanges()
    },

    [apiRef, entity?.id, updateViewCellField, createViewCellField, columns, hasExternalActionData]
  )

  const handleSetDataOnVariableChangeEvent = data => {
    if (!data) return

    const transformedExternalData = getTransformResponse(data)

    setExternalActionData(transformedExternalData)
  }

  const handleGetRowClassName = (params: GridRowClassNameParams<any>) => {
    let defaultClassName = ''
    const rowId =
      params.row._id ?? params.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
    const viewRecords = entity?.id ? objectsWithDirtyRecords[objectCode]?.[entity.id] : undefined
    const row = viewRecords ? viewRecords[rowId] : undefined

    if (theme?.palette?.mode === 'light') {
      defaultClassName = params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
    }

    if (row && row === EVENT_NAME_OBJECT_MESSAGE.CREATE) {
      return UPDATES_ROW_CLASSNAME
    }

    if (row && row === EVENT_NAME_OBJECT_MESSAGE.DELETE) {
      return DELETED_ROW_CLASSNAME
    }

    if (highlightedSelectedRowsIds.includes(rowId)) {
      return SELECTED_ROW_CLASSNAME
    }

    if (prevSelectedRowId === rowId) {
      return PREV_SELECTED_ROW_CLASSNAME
    }

    return defaultClassName
  }

  // в зависимости от правила и если чекнут столбец
  const changeBackgroundCell = (
    valueCell: string,
    sortKey: string,
    columnValue: string
  ): boolean => {
    const checkedColumn = columnNameCheckBox[columnValue]

    if (checkedColumn) {
      switch (searchRule.name) {
        case SEARCH_RULE_TYPE.EQUAL:
          return Boolean(valueSearchFilter) && valueSearchFilter == valueCell

        case SEARCH_RULE_TYPE.NOT_EQUAL:
          return Boolean(valueSearchFilter) && valueSearchFilter != valueCell

        case SEARCH_RULE_TYPE.CONTAIN:
          return (
            Boolean(valueCell) &&
            Boolean(valueSearchFilter) &&
            valueCell.toLowerCase().includes(valueSearchFilter.toLowerCase())
          )

        case SEARCH_RULE_TYPE.NOT_CONTAIN:
          return (
            Boolean(valueCell) &&
            Boolean(valueSearchFilter) &&
            !valueCell.toLowerCase().includes(valueSearchFilter.toLowerCase())
          )

        case SEARCH_RULE_TYPE.EMPTY:
          return !isNotEmpty(valueCell)

        case SEARCH_RULE_TYPE.NOT_EMPTY:
          return isNotEmpty(valueCell)

        case SEARCH_RULE_TYPE.STARTS_WITH:
          return (
            Boolean(valueCell) &&
            Boolean(valueSearchFilter) &&
            valueCell.startsWith(valueSearchFilter)
          )

        case SEARCH_RULE_TYPE.ENDS_WITH:
          return (
            Boolean(valueCell) &&
            Boolean(valueSearchFilter) &&
            valueCell.endsWith(valueSearchFilter)
          )

        case SEARCH_RULE_TYPE.LESS:
          return Boolean(valueSearchFilter) && Number(valueCell) < Number(valueSearchFilter)

        case SEARCH_RULE_TYPE.MORE:
          return Boolean(valueSearchFilter) && Number(valueCell) > Number(valueSearchFilter)

        case SEARCH_RULE_TYPE.LESS_OR_EQUAL:
          return Boolean(valueSearchFilter) && Number(valueCell) <= Number(valueCell)

        case SEARCH_RULE_TYPE.MORE_OR_EQUAL:
          return Boolean(valueSearchFilter) && Number(valueCell) >= Number(valueSearchFilter)

        default:
          return false
      }
    }

    return false
  }

  const handleGetCellClassName = (params: GridCellParams<ObjectDataRecord, ObjectDataRecord>) => {
    const rowId =
      params.row._id ?? params.row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
    const columnValue = params.colDef.field ?? params.colDef?.columnValue
    const sortKey = params.colDef?.sortKey
    const cellValue = columnValue && params.row[columnValue]
    const objectValue = params.colDef?.columnObjectValue

    const viewRecords = entity?.id ? objectsWithDirtyRecords[objectCode]?.[entity.id] : undefined
    const dirtyFields = viewRecords ? viewRecords[rowId] : undefined

    const currentRow = unsavedChangesRef.current.rows[rowId]
    const originalRow = unsavedChangesRef.current.originalRows[rowId]

    const hasProperty = !!currentRow && Object.hasOwn(currentRow, columnValue)
    const isValueChanged = hasProperty && currentRow?.[columnValue] !== originalRow?.[columnValue]
    const isEdited = hasProperty && isValueChanged

    const isPrefilled = currentRow?.isNew && prefillData && Object.hasOwn(prefillData, columnValue)
    const isPrefillChanged = currentRow?.[columnValue] !== prefillData?.[columnValue]

    const errorKey = `${rowId}.${columnValue}`

    if (errorFields[errorKey]) {
      return 'cell--error'
    }

    if (isPrefilled && !isPrefillChanged) {
      return 'cell--prefilled'
    }

    if ((isEditMode || hasNewRows) && isEdited) {
      return 'cell--edited'
    }

    const valueCell =
      cellValue && objectValue
        ? renderObjectEmbeddedCell(columnValue, params.row)
        : isUndefined(cellValue) || isNull(cellValue)
        ? cellValue
        : String(cellValue)

    if (changeBackgroundCell(valueCell, sortKey, columnValue)) {
      return FOUND_SEARCH_VALUE_CLASSNAME
    }

    if (
      dirtyFields &&
      Array.isArray(dirtyFields) &&
      columnValue &&
      localUpdateType === UPDATE_TYPE.WEBSOCKET
    ) {
      // совпадение в измененных полях
      const hasDirtyField = dirtyFields.includes(columnValue)
      if (hasDirtyField) {
        return UPDATES_ROW_CLASSNAME
      }
    }

    return ''
  }

  const handleGetRowHeight = () => {
    if (hasWrapRowText) {
      return 'auto'
    }

    return DATAGRID_ROW_WINDOW_HEIGHT
  }

  const handleObjectSubscribe = useObjectSubscribeWS({
    objectCode,
    entity,
    objectData,
    currentSort,
    setObjectData,
    contextId,
    searchAssistantValue,
    hasQuickSearch,
    entityParamsForRequest,
    setTotalElements,
    setSelectedRows,
  })

  const [isAutosizingColumns, setIsAutosizingColumns] = useState(false)
  const [isResettingRestrictions, setIsResettingRestrictions] = useState(false)
  const isAutosizingColumnsRef = useRef(false)
  const isResettingRestrictionsRef = useRef(false)

  const setAutosizingColumns = useCallback((value: boolean) => {
    isAutosizingColumnsRef.current = value
    setIsAutosizingColumns(value)
  }, [])

  const setResettingRestrictions = useCallback((value: boolean) => {
    isResettingRestrictionsRef.current = value
    setIsResettingRestrictions(value)
  }, [])
  const pendingColumnVisibilityModelRef = useRef<GridColumnVisibilityModel>(
    initialColumnVisibilityModel
  )
  const lastPersistedColumnVisibilityModelRef = useRef<GridColumnVisibilityModel>(
    initialColumnVisibilityModel
  )
  const visibilityUpdateSeqRef = useRef(0)
  const visibilityFieldCodes = useMemo(() => viewColumns.map(column => column.code), [viewColumns])

  useEffect(() => {
    const normalized = normalizeColumnVisibilityModel(initialColumnVisibilityModel, visibilityFieldCodes)

    pendingColumnVisibilityModelRef.current = normalized
    lastPersistedColumnVisibilityModelRef.current = normalized
  }, [initialColumnVisibilityModel, visibilityFieldCodes])

  const shouldSkipRestrictionPersistence = () =>
    isAutosizingColumnsRef.current || isResettingRestrictionsRef.current

  const handleAutosizeColumns = useCallback(async () => {
    const api = apiRef.current

    if (!api?.autosizeColumns || isAutosizingColumnsRef.current || isResettingRestrictionsRef.current) {
      return
    }

    setAutosizingColumns(true)

    try {
      await api.autosizeColumns({
        includeHeaders: true,
        includeOutliers: true,
      })

      const widthRestrictions: RestrictionDTO[] = (api.getVisibleColumns?.() ?? api.getAllColumns())
        .filter(col => col.field && col.field !== SYSTEM_SELECT_COLUMN)
        .map(col => ({
          fieldName: col.headerName ?? col.field,
          fieldCode: col.field,
          viewId: entityId as string,
          restrictionType: RESTRICTION_TYPE.WIDTH,
          value: Math.min(
            MAX_VIEW_COLUMN_WIDTH,
            Math.max(
              MIN_VIEW_COLUMN_WIDTH,
              Math.ceil(col.width ?? col.computedWidth ?? MIN_VIEW_COLUMN_WIDTH)
            )
          ),
        }))

      if (widthRestrictions.length) {
        await handleUpdateRestrictions(widthRestrictions)
      }
    } finally {
      setAutosizingColumns(false)
    }
  }, [apiRef, entityId, handleUpdateRestrictions, setAutosizingColumns])

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    if (!params || params.colDef.field === SYSTEM_SELECT_COLUMN || shouldSkipRestrictionPersistence()) {
      return
    }

    handleUpdateRestrictions([
      {
        fieldName: params.colDef.headerName,
        fieldCode: params.colDef.field,
        viewId: entityId as string,
        restrictionType: RESTRICTION_TYPE.WIDTH,
        value: Math.min(
          MAX_VIEW_COLUMN_WIDTH,
          Math.max(MIN_VIEW_COLUMN_WIDTH, Math.ceil(params.width))
        ),
      },
    ])
  }

  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>()

  const handleSetPinnedColumns = (value: GridPinnedColumnFields) => {
    setPinnedColumns(value)
  }

  const handlePinnedColumnsChange = (pinnedColumns: GridPinnedColumnFields) => {
    if (entityRestrictions === undefined || shouldSkipRestrictionPersistence()) {
      return
    }

    const currentPinnedColumns = transformFixRestrictions(entityRestrictions.fix ?? [])

    // Поиск и преобразование новых закрепленных колонок
    const newPinnedColumns = findPinnedColumnsDifferences(
      entityRestrictions.fix ?? [],
      entityId as string,
      currentPinnedColumns,
      pinnedColumns
    )

    // Поиск и преобразование колонок, которые были откреплены
    const unpinnedColumns = findPinnedColumnsDifferences(
      entityRestrictions.fix ?? [],
      entityId as string,
      pinnedColumns,
      currentPinnedColumns,
      null,
      newPinnedColumns
    )

    const foundRestrictions: RestrictionDTO[] = [...unpinnedColumns, ...newPinnedColumns]
    const restrictionsToChange: RestrictionDTO[] = []

    const internalIds = getColumnsInternalIds(columns)

    foundRestrictions.forEach((updatedRestriction: RestrictionDTO) => {
      const columnInView = entity?.columns?.find(
        column => column.code === updatedRestriction.fieldCode
      )

      if (isColumnPinnedByAdmin(columnInView?.code, defaultEntityRestrictions)) {
        return
      }

      restrictionsToChange.push({
        ...updatedRestriction,
        internalId: internalIds[updatedRestriction.fieldName],
      })
    })

    if (restrictionsToChange.length > 0) {
      setPinnedColumns(pinnedColumns)

      const syncOrderAfterPin = () => {
        const { orderRestrictions, changedOrders } = buildOrderRestrictionsFromVisibleColumns(
          apiRef,
          entityId as string,
          entityRestrictions
        )

        if (!changedOrders.length) {
          return Promise.resolve()
        }

        return handleUpdateRestrictions(changedOrders, orderRestrictions)
      }

      handleUpdateRestrictions(restrictionsToChange).then(syncOrderAfterPin)
    } else if (foundRestrictions.length > 0) {
      alert(t('configEntity.view.fixedByAdmin'))
    } else {
      setPinnedColumns(pinnedColumns)
    }
  }

  const handleResetRestrictions = useCallback(async () => {
    if (isResettingRestrictionsRef.current || isAutosizingColumnsRef.current) {
      return
    }

    setResettingRestrictions(true)

    try {
      handleSetColumnNameCheckBox({})
      handleSetSortKeysColumnCheckBox({})
      handleSetSortKeysForQuickSearch([])

      const newData = await removeAllRestrictions()

      if (newData?.sort) {
        refreshChangeSortData(
          newData.sort
            .filter(sortItem =>
              viewColumns.some(col => col.code === sortItem.fieldCode && col.visibility)
            )
            .map(sortItem => ({
              field: sortItem.fieldCode,
              sortKey: sortItem.value.columnToSort,
              priority: sortItem.value.priority,
              sort: sortItem.value.sortDirection,
            }))
            ?.sort((sortA, sortB) => sortA.priority - sortB.priority)
        )
      }
    } finally {
      window.setTimeout(() => {
        setResettingRestrictions(false)
      }, 250)
    }
  }, [
    removeAllRestrictions,
    viewColumns,
    handleSetColumnNameCheckBox,
    handleSetSortKeysColumnCheckBox,
    handleSetSortKeysForQuickSearch,
    refreshChangeSortData,
    setResettingRestrictions,
  ])

  const handleHideColumnsDebounce = useCallback(
    useDebounceFn({
      fn: () => {
        if (isResettingRestrictionsRef.current) {
          return
        }

        const newModel = normalizeColumnVisibilityModel(
          pendingColumnVisibilityModelRef.current,
          visibilityFieldCodes
        )
        const prevModel = normalizeColumnVisibilityModel(
          lastPersistedColumnVisibilityModelRef.current,
          visibilityFieldCodes
        )
        const resultModel = compareModels(prevModel, newModel)

        if (isEqual(prevModel, newModel) || Object.keys(resultModel).length === 0) {
          return
        }

        const columnNameCheckBoxSnapshot = columnNameCheckBoxRef.current
        const sortKeysColumnCheckBoxSnapshot = sortKeysColumnCheckBoxRef.current

        let mergedColumnNameCheckBox = { ...columnNameCheckBoxSnapshot }
        let mergedSortKeysColumnCheckBox = { ...sortKeysColumnCheckBoxSnapshot }
        const columnVisibModel: RestrictionDTO[] = []

        Object.keys(resultModel).forEach(field => {
          const column = viewColumns.find(col => field === col.code)
          const isVisible = Boolean(resultModel[field])

          columnVisibModel.push({
            fieldCode: field,
            viewId: entityId as string,
            restrictionType: RESTRICTION_TYPE.SHOW,
            value: isVisible,
          })

          if (
            column &&
            column.code in columnNameCheckBoxSnapshot &&
            column.title in sortKeysColumnCheckBoxSnapshot
          ) {
            mergedColumnNameCheckBox = {
              ...mergedColumnNameCheckBox,
              [column.code]:
                getIsAvailableRuleByType(column, searchRule.name) &&
                column.valueType !== FIELD_VALUE_TYPE.BOOLEAN
                  ? isVisible
                  : false,
            }

            mergedSortKeysColumnCheckBox = {
              ...mergedSortKeysColumnCheckBox,
              [column.code]:
                getIsAvailableRuleByType(column, searchRule.name) &&
                column.valueType !== FIELD_VALUE_TYPE.BOOLEAN
                  ? isVisible
                  : false,
            }
          }
        })

        columnNameCheckBoxRef.current = mergedColumnNameCheckBox
        sortKeysColumnCheckBoxRef.current = mergedSortKeysColumnCheckBox

        handleSetColumnNameCheckBox(mergedColumnNameCheckBox)
        handleSetSortKeysColumnCheckBox(mergedSortKeysColumnCheckBox)
        setSortKeysForQuickSearch(getSortKeysOfColumnsForQuickSearch(mergedSortKeysColumnCheckBox))

        lastPersistedColumnVisibilityModelRef.current = newModel
        void handleUpdateRestrictions(columnVisibModel)
      },
      delay: 200,
    }),
    [
      viewColumns,
      searchRule,
      entityId,
      visibilityFieldCodes,
      handleUpdateRestrictions,
      handleSetColumnNameCheckBox,
      handleSetSortKeysColumnCheckBox,
      setSortKeysForQuickSearch,
    ]
  )

  const handleChangeColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
    if (shouldSkipRestrictionPersistence()) {
      return
    }

    const updateSeq = ++visibilityUpdateSeqRef.current
    const normalizedModel = normalizeColumnVisibilityModel(model, visibilityFieldCodes)

    pendingColumnVisibilityModelRef.current = normalizedModel

    onSetColumnVisibilityModel(prevModel =>
      updateSeq === visibilityUpdateSeqRef.current ? normalizedModel : prevModel
    )

    handleHideColumnsDebounce()
  }

  const handleHideColumnMenu = () => {
    if (apiRef.current && apiRef.current.hideColumnMenu) {
      apiRef.current.hideColumnMenu()
    }
  }

  const handleSetInitialSortingFromRestrictions = (viewColumns: ConfiguredColumn[]) => {
    const initialSorting: SortType[] = getSortByColumns(viewColumns || [])

    // Применение пользовательской сортировки при открытии сущности
    refreshChangeSortData(initialSorting)
  }

  useScrollDialogContainer({
    dialogId,
    apiRef,
    onScrollDraggingDialogContainer: handleHideColumnMenu,
  })

  const handleRefreshAllPages = () => {
    const hasEdits = Object.keys(apiRef.current.state.editRows).length > 0
    if (hasEdits) {
      setHasPendingRefresh(true)

      return
    }

    setIsUpdateLoading(true)
    refreshAllPages().finally(() => setIsUpdateLoading(false))
  }

  const applySortUserRestrictionsAsDefault = async (viewId: string) => {
    if (entityRestrictions?.sort?.length) {
      const updates = entityRestrictions.sort?.map(r => ({
        viewId,
        fieldCode: r.fieldCode,
        fieldName: r.fieldName,
        restrictionType: RESTRICTION_TYPE.SORT,
        value: `${r.value.columnToSort}//${r.value.sortDirection}//${r.value.priority || 0}`,
      }))

      await updateDefaultRestrictions(updates, undefined, true)

      return
    }

    if (!entityRestrictions?.sort?.length && defaultEntityRestrictions?.sort?.length) {
      await removeDefaultRestrictions(RESTRICTION_TYPE.SORT)
    }
  }

  const handleUseUserRestrictionsAsDefault = useCallback(async () => {
    handleCloseUserRestrictionAsDefaultModel()
    try {
      if (!entityRestrictions || !entityId) return

      const viewId = String(entityId)

      await applySortUserRestrictionsAsDefault(viewId)

      if (entityRestrictions.width?.length) {
        const updates = entityRestrictions.width.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.WIDTH,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates)
      }

      if (entityRestrictions.show?.length) {
        const updates = entityRestrictions.show.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.SHOW,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates)
      }

      if (entityRestrictions.order?.length) {
        const updates = entityRestrictions.order.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.ORDER,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates)
      }

      if (entityRestrictions.fix?.length) {
        const updates = entityRestrictions.fix.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.FIX,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates)
      }

      if (entityRestrictions.quickSearchChoice?.length) {
        const updates = entityRestrictions.quickSearchChoice.map(r => ({
          viewId,
          fieldCode: r.fieldCode,
          fieldName: r.fieldName,
          restrictionType: RESTRICTION_TYPE.QUICK_SEARCH_CHOICE,
          value: r.value,
        }))
        await updateDefaultRestrictions(updates, undefined, undefined, true)
      }
    } catch (error) {
      console.error(error)
    }
  }, [entityId, entityRestrictions, updateDefaultRestrictions])

  return {
    state: {
      isShowDeleteModal,
      isCtrlHeld,
      isShiftHeld,
      selectedRowIds,
      selectedRowIdsSet,
      samePageFormsData,
      currentStaticForm,
      currentDynamicForm,
      isOpenShowColumnsSettings,
      columnNameCheckBox,
      sortKeysForQuickSearch,
      sortKeysColumnCheckBox,
      pinnedColumns,
      scrollingParams,
      isLoadingRestrictions,
      searchAssistantValue,
      valueSearchFilter,
      searchRule,
      isBooleanSearchMode,
      isUpdateLoading,
      previewFileUrl,
      showPreviewFileModal,
      isEditMode,
      hasUnsavedChanges,
      hasNewRows,
      hasExternalActionData,
      allowEditMode,
      hasErrorFields,
      isActiveTimeWindow,
      hasOuterOnRowSelectionEvent,
      hasOnRowSelectionEvent,
      hasDragStartEvent,
      isShowUserRestrictionAsDefaultModel,
      activeDownloadRows,
      isAutosizingColumns,
      isResettingRestrictions,
      ...dragAndDropRowStates,
    },
    data: {
      currentSort,
      objectData: mappedObjectData,
      externalActionData,
      newRows,
      selectedRows,
      unsavedChangesRef,
      totalPages,
      totalCountElements,
      totalElements,
      valueSearchFilter,
      searchRule,
      currentPage,
      isLoadingObjectData,
      hasWrapRowText,
      hasWrapHeaderText,
      headerHeight,
      headerGroupHeight,
      isEnabledLocalParameters,
    },
    handlers: {
      setIsUpdateLoading,
      refreshObjectData,
      handleChangeModelSort,
      handleClickSearchFilter,
      handleSetIsCtrlHeld,
      handleSetIsShiftHeld,
      handleSetSelectedRowIds: handleSetSelectedRows,
      handleStaticFormChange,
      handleDynamicFormChange,
      handleCloseCurrentFormTab,
      handleClearSamePageForms,
      handleEdit,
      handleCellEditStop,
      handleClickDelete,
      handleCloseDeleteModal,
      handleClosePreviewFileModal,
      handleAdditionalButtonClick,
      handleDownloadButtonClick,
      handlePreviewButtonClick,
      handleRowButtonClick,
      handleRowClick,
      handleCellKeyDown,
      handleCellClick,
      handleCellDoubleClick,
      handleCreate,
      handleGetRowClassName,
      handleGetRowHeight,
      handleObjectDataPageChange,
      handleSetObjectData,
      handleGetCellClassName,
      handleObjectSubscribe,
      handleSetTotalElements,
      handleOpenShowColumnsSettings,
      handleColumnWidthChange,
      handleAutosizeColumns,
      handleResetRestrictions,
      handlePinnedColumnsChange,
      handleChangeColumnVisibilityModel,
      handleSetInitialSortingFromRestrictions,
      refreshChangeSortData,
      handleSetGlobalFilter,
      handleSetColumnNameCheckBox,
      handleSetSearchRule,
      handleSetSortKeysForQuickSearch,
      handleSetSortKeysColumnCheckBox,
      handleSetPinnedColumns,
      handleSetBooleanMode,
      handleRefreshAllPages,
      setIsEditMode,
      handleViewRowUpdate,
      handleDiscardViewEditChanges,
      handleSaveViewEditChanges,
      handleAddEmptyRow,
      handleErrorFields: setErrorFields,
      handleSetDataOnVariableChangeEvent,
      setSelectedRows,
      handleUseUserRestrictionsAsDefault,
      handleCloseUserRestrictionAsDefaultModel,
      handleShowUserRestrictionAsDefaultModel,
      handleSetLocalUpdateType,
      ...dragAndDropRowHandlers,
    },
  }
}
